import { createContext, type ReactNode, useContext } from 'react';
import { Conversation } from '~api';
import { api } from '~portal/api';
import { trackAction } from '~portal/lib/product-analytics';

type ChatAccess = {
  /** Viewer-relative access stamped by the backend on the loaded conversation. */
  access: Conversation.Access;
  /** Convenience flag — true when an admin is viewing another user's conversation. */
  isReadOnly: boolean;
};

const ChatAccessContext = createContext<ChatAccess>({
  access: Conversation.Access.Owner,
  isReadOnly: false,
});

/**
 * @desc Single source of truth for whether the current conversation is read-only.
 * The backend decides ownership and stamps `access` on the `get` response; this
 * provider surfaces it to the chat layout (delete button) and the chat page
 * (banner, composer) so no component re-derives ownership from `refs.userId`.
 *
 * Runs its own `get` query, but React Query dedupes the identical key with the
 * page's `useConversation` fetch — one request, shared cache.
 */
export function ChatAccessProvider({
  conversationId,
  children,
}: {
  conversationId: string | undefined;
  children: ReactNode;
}) {
  const isNew = !conversationId || conversationId === 'new';

  const { data } = api.conversations.get.useQuery(
    { conversationId: conversationId ?? '' },
    { enabled: !isNew, staleTime: 0, refetchOnWindowFocus: false },
  );

  // Default to owner while loading or for new conversations so controls render
  // optimistically; only a confirmed `readonly` from the backend gates them.
  const access = data?.access ?? Conversation.Access.Owner;
  const value: ChatAccess = { access, isReadOnly: access === Conversation.Access.ReadOnly };

  return <ChatAccessContext.Provider value={value}>{children}</ChatAccessContext.Provider>;
}

export function useChatAccess() {
  return useContext(ChatAccessContext);
}

/**
 * @desc Report an artefact download to Datadog RUM — for the conversation's owner only.
 *
 * `artifact_downloaded` measures what users take out of the product, so an admin saving a file
 * from another user's read-only transcript must not count. The suppression reads the same
 * backend-stamped access as the banner and the composer, which means every download surface
 * that renders inside a chat inherits it without a prop, and a new surface cannot forget it.
 * The Inspector's own download button never calls this: it does not go through these components.
 */
export function useTrackArtifactDownload() {
  const { isReadOnly } = useChatAccess();
  return (format: string, surface: 'table' | 'card' | 'chip' | 'viewer') => {
    if (isReadOnly) return;
    trackAction('artifact_downloaded', { format, surface });
  };
}
